import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, Chip, Grid, Paper, Stack, Divider} from 'views/components';
import {Favorite, Info} from 'views/icons';
import {Translate, translate, MarkdownEditor} from 'views/module';

const CareSpecifications = (props) => {
  const {newJob, updateJobCarePlan, updateJobConsiderations, setIsStepValid} = props;

  const carePlan = newJob.carePlan || {};
  const selectedCareNeeds = carePlan.careNeeds || [];

  const considerations = newJob.considerations || {};
  const selectedConsiderationOptions = considerations.options || [];
  const additionalConsiderations = considerations.additionalConsiderations?.blob || '';

  // Care options array - get keys from translation object
  const careOptionsObject = translate('career.create.CarePlan.Options');
  const careOptions = Object.keys(careOptionsObject);

  // Considerations options array - get keys from translation object
  const considerationOptionsObject = translate('career.create.Considerations.Options');
  const considerationOptions = Object.keys(considerationOptionsObject);

  // Update step validity when care needs change
  React.useEffect(() => {
    const isValid = selectedCareNeeds.length > 0;
    setIsStepValid(isValid);
  }, [selectedCareNeeds, setIsStepValid]);

  const handleCareNeedToggle = (careNeed) => {
    updateJobCarePlan((carePlan) => {
      const currentNeeds = carePlan.careNeeds || [];
      const isSelected = currentNeeds.includes(careNeed);

      const updatedNeeds = isSelected ?
        currentNeeds.filter((need) => need !== careNeed) :
        [...currentNeeds, careNeed];

      return {
        ...carePlan,
        careNeeds: updatedNeeds,
      };
    });
  };

  const handleConsiderationOptionToggle = (option) => {
    updateJobConsiderations((considerations) => {
      const currentOptions = considerations.options || [];
      const isSelected = currentOptions.includes(option);

      const updatedOptions = isSelected ?
        currentOptions.filter((opt) => opt !== option) :
        [...currentOptions, option];

      return {
        ...considerations,
        options: updatedOptions,
      };
    });
  };

  const handleAdditionalConsiderationsChange = (event) => {
    const value = event;
    updateJobConsiderations((considerations) => ({
      ...considerations,
      additionalConsiderations: {
        blob: value,
        editorType: 'MARKDOWN',
      },
    }));
  };

  const getSelectedCareDescriptions = () => {
    return selectedCareNeeds.map((need) => ({
      key: need,
      label: translate(`career.create.CarePlan.Options.${need}.Label`),
      description: translate(`career.create.CarePlan.Options.${need}.Description`),
    }));
  };

  const getSelectedConsiderationDescriptions = () => {
    return selectedConsiderationOptions.map((option) => ({
      key: option,
      label: translate(`career.create.Considerations.Options.${option}`),
    }));
  };

  return (
    <Box sx={{p: 2, maxWidth: 1200, mx: 'auto'}}>
      <Typography variant="h4" gutterBottom sx={{textAlign: 'center', mb: 4}}>
        <Translate expression="career.create.CarePlan.Title" />
      </Typography>

      {/* Care Needs Section */}
      <Box sx={{mb: 6}}>
        <Box sx={{mb: 4, textAlign: 'center'}}>
          <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 500}}>
            <Translate expression="career.create.CarePlan.CareNeedsLabel" />
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
            <Translate expression="career.create.CarePlan.CareNeedsDescription" />
          </Typography>

          {/* Care Options Grid */}
          <Grid container spacing={1.5} justifyContent="center">
            {
              careOptions.map((option) => {
                const isSelected = selectedCareNeeds.includes(option);
                const label = translate(`career.create.CarePlan.Options.${option}.Label`);

                return (
                  <Grid item key={option}>
                    <Chip
                      label={label}
                      onClick={() => handleCareNeedToggle(option)}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                    />
                  </Grid>
                );
              })
            }
          </Grid>
        </Box>

        {/* Selected Care Needs Summary */}
        {
          selectedCareNeeds.length > 0 &&
          <Box sx={{textAlign: 'center'}}>
            <Typography variant="subtitle1" sx={{mb: 2, fontWeight: 500}}>
              <Translate expression="career.create.CarePlan.SelectedCareNeedsTitle" />
            </Typography>

            <Box sx={{maxWidth: 900, mx: 'auto', textAlign: 'left'}}>
              {/* Individual descriptions */}
              <Box sx={{mb: 3}}>
                {
                  getSelectedCareDescriptions().map((item) => (
                    <Typography key={item.key} variant="body2" sx={{mb: 1}}>
                      <Typography component="span" sx={{fontWeight: 600}}>{item.label}:</Typography> {item.description}
                    </Typography>
                  ))
                }
              </Box>

              {/* Summary box */}
              <Box
                sx={{
                  p: 3,
                  backgroundColor: '#fdf2f8',
                  borderRadius: 2,
                  border: '1px solid #fce7f3',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                }}
              >
                <Favorite sx={{color: '#ec4899', mt: 0.5}} />
                <Box>
                  <Typography variant="subtitle2" sx={{fontWeight: 600, color: '#ec4899', mb: 0.5}}>
                    <Translate
                      expression="career.create.CarePlan.SelectedCareNeedsCount"
                      substitute={{count: selectedCareNeeds.length}}
                    />
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getSelectedCareDescriptions().map((item) => item.label).join(', ')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        }
      </Box>

      {/* Divider between sections */}
      <Divider sx={{my: 6}} />

      {/* Special Considerations Section */}
      <Box>
        <Typography variant="h5" gutterBottom sx={{textAlign: 'center', mb: 3}}>
          <Translate expression="career.create.Considerations.Title" />
        </Typography>

        {/* Common Considerations Selection */}
        <Box sx={{mb: 4, textAlign: 'center'}}>
          <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 500}}>
            <Translate expression="career.create.Considerations.CommonConsiderationsLabel" />
          </Typography>

          {/* Considerations Options Grid */}
          <Grid container spacing={1.5} justifyContent="center">
            {
              considerationOptions.map((option) => {
                const isSelected = selectedConsiderationOptions.includes(option);
                const optionLabel = translate(`career.create.Considerations.Options.${option}`);

                return (
                  <Grid item key={option}>
                    <Chip
                      label={optionLabel}
                      onClick={() => handleConsiderationOptionToggle(option)}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                    />
                  </Grid>
                );
              })
            }
          </Grid>
        </Box>

        {/* Additional Considerations Text Area */}
        <Box sx={{mb: 4, textAlign: 'center'}}>
          <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 500}}>
            <Translate expression="career.create.Considerations.AdditionalConsiderationsLabel" />
          </Typography>
          <Box sx={{maxWidth: 900, mx: 'auto'}}>
            <MarkdownEditor
              value={additionalConsiderations}
              onChange={handleAdditionalConsiderationsChange}
              height={150}
            />
          </Box>
        </Box>

        {/* Considerations Preview Summary */}
        {
          (selectedConsiderationOptions.length > 0 || additionalConsiderations.trim()) &&
          <Box sx={{maxWidth: 900, mx: 'auto'}}>
            <Paper
              sx={{
                p: 3,
                backgroundColor: '#fdf2f8',
                borderRadius: 2,
                border: '1px solid #fce7f3',
              }}
            >
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <Info sx={{color: '#ec4899', mt: 0.5}} />
                <Box sx={{width: '100%'}}>
                  {/* Selected Options */}
                  {
                    selectedConsiderationOptions.length > 0 &&
                      <Box sx={{mb: additionalConsiderations.trim() ? 2 : 0}}>
                        <Typography variant="subtitle2" sx={{fontWeight: 600, color: '#ec4899', mb: 1}}>
                          <Translate expression="career.create.Considerations.SelectedLabel" />:
                        </Typography>
                        <Box>
                          {
                            getSelectedConsiderationDescriptions().map((item) => (
                              <Typography key={item.key} variant="body2" sx={{mb: 0.5}}>
                                {item.label}
                              </Typography>
                            ))
                          }
                        </Box>
                      </Box>
                  }

                  {/* Additional Considerations */}
                  {
                    additionalConsiderations.trim() &&
                      <Box>
                        {selectedConsiderationOptions.length > 0 && <Divider sx={{my: 2}} />}
                        <Typography variant="subtitle2" sx={{fontWeight: 600, color: '#ec4899', mb: 1}}>
                          <Translate expression="career.create.Considerations.AdditionalConsiderationsLabel" />:
                        </Typography>
                        <Typography variant="body2" sx={{whiteSpace: 'pre-wrap'}}>
                          {additionalConsiderations}
                        </Typography>
                      </Box>
                  }
                </Box>
              </Stack>
            </Paper>
          </Box>
        }

        {/* Empty State for Considerations */}
        {
          selectedConsiderationOptions.length === 0 && !additionalConsiderations.trim() &&
            <Box sx={{maxWidth: 900, mx: 'auto'}}>
              <Paper
                sx={{
                  p: 3,
                  backgroundColor: '#f9fafb',
                  borderRadius: 2,
                  border: '1px solid #e5e7eb',
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  <Translate expression="career.create.Considerations.NoConsiderationsSelected" />
                </Typography>
              </Paper>
            </Box>
        }
      </Box>
    </Box>
  );
};

CareSpecifications.propTypes = {
  newJob: PropTypes.object.isRequired,
  updateJobCarePlan: PropTypes.func.isRequired,
  updateJobConsiderations: PropTypes.func.isRequired,
  setIsStepValid: PropTypes.func.isRequired,
};

export default CareSpecifications;
